import { useInboxMessageQuery } from "@qw/graphql";
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@qw/ui";
import { type FC, useMemo } from "react";

import { useDraft } from "../../providers/draft-provider";
import { DraftEditorReferenceMessageForward } from "./draft-editor-reference-message-forward";
import { DraftEditorReferenceMessageReply } from "./draft-editor-reference-message-reply";

export interface DraftEditorReferenceMessageProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DraftEditorReferenceMessage: FC<DraftEditorReferenceMessageProps> = ({ open, onOpenChange }) => {
  const { draft } = useDraft();
  const { data, loading, error } = useInboxMessageQuery({
    variables: {
      input: { messageId: draft?.referenceMessageId || "" },
    },
    skip: !draft?.referenceMessageId,
  });

  const isForward = useMemo(() => draft?.subject?.startsWith("Fwd: "), [draft?.subject]);

  const tooltipText = useMemo(
    () => `${open ? "Hide" : "Show"} ${isForward ? "forwarded" : "quoted"} message`,
    [isForward, open]
  );

  if (!data?.inboxMessage) {
    return null;
  }

  return (
    <div className="flex-0">
      <Collapsible defaultOpen={false} open={open} onOpenChange={onOpenChange}>
        <Tooltip>
          <CollapsibleTrigger asChild disabled={loading || !!error}>
            <TooltipTrigger asChild>
              <Button variant="secondary" size="xs" className="rounded-full py-0 px-3 h-5">
                <Icon icon="more_horiz" />
              </Button>
            </TooltipTrigger>
          </CollapsibleTrigger>
          <TooltipContent>{tooltipText}</TooltipContent>
        </Tooltip>
        <CollapsibleContent className="pt-2">
          <div className="tiptap text-sm">
            {isForward ? (
              <DraftEditorReferenceMessageForward message={data.inboxMessage} />
            ) : (
              <DraftEditorReferenceMessageReply message={data.inboxMessage} />
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
